import { ArrowLeft, Printer, Tag, X } from 'lucide-react'
import { Badge } from '@univdiam/ui/components/badge'
import StatusDropDown from './StatusDropDown'
import type { SelectedOptionProps } from '@univdiam/ui/type';
import { options } from '@univdiam/ui/lib/StatusOptions';
import { useState, useRef, useEffect } from 'react';
import AddLabels, { type AdminTag } from '../OrderList/AddLabels';
import { labelStyle } from '@univdiam/ui/lib/tagColors'
import DueDatePicker from './DueDatePicker'

type orderDetailProps = {
  orderDetail: {
    id: string;
    JobTitle: string;
    status: string;
    order_id: string;
    customer_id: string
    designType?: string | null
  },
  labels?: string[],
  /** `yyyy-MM-dd` (or a timestamp); undefined/null renders as "Set due date". */
  dueDate?: string | null,
  adminTags?: AdminTag[],
  fetchTags?: (page: number, limit: number, search: string) => Promise<{ tags: AdminTag[]; totalPages: number }>,
  onUpdateOrder?: (order_id: string, customer_id: string, updates: { labels?: string[]; status?: string; is_new_update?: boolean; due_date?: string | null; orderRef: string }) => void,
  handleBack: () => void;
  updateOrderStatus: (params: { order_id: string; customer_id: string; status: string, orderRef: string }) => void;
  handlePrint?: () => void;
}

function AdminHeader({ orderDetail, labels = [], dueDate, adminTags = [], fetchTags, onUpdateOrder, handleBack, updateOrderStatus, handlePrint }: orderDetailProps) {
  const defaultOption = options.find(o => o.id === orderDetail.status) ?? options[0]
  const [selectedValue, setSelectedValue] = useState<SelectedOptionProps>(defaultOption!);
  const [showTagPopover, setShowTagPopover] = useState(false);
  const [positionAbove, setPositionAbove] = useState(false);
  const tagButtonRef = useRef<HTMLDivElement>(null);

  // Shown value is local so the chip recolours on pick instead of waiting for
  // the save round-trip; it re-syncs whenever the server value actually changes.
  const [localDueDate, setLocalDueDate] = useState<string | null>(dueDate ?? null);
  useEffect(() => {
    setLocalDueDate(dueDate ?? null);
  }, [dueDate]);

  const handleDueDateChange = (value: string | null) => {
    setLocalDueDate(value);
    onUpdateOrder?.(orderDetail.id, orderDetail.customer_id, {
      due_date: value,
      orderRef: orderDetail.order_id,
    });
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (tagButtonRef.current && !tagButtonRef.current.contains(e.target as Node)) {
        setShowTagPopover(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleTagClick = () => {
    if (!showTagPopover && tagButtonRef.current) {
      const rect = tagButtonRef.current.getBoundingClientRect();
      setPositionAbove(window.innerHeight - rect.bottom < 300);
    }
    setShowTagPopover(prev => !prev);
  };


  //casuing error -  each time order opens it will update the status
  // useEffect(() => {
  //   if (orderDetail && selectedValue) {
  //     updateOrderStatus({
  //       order_id: orderDetail.order_id,
  //       customer_id: orderDetail.customer_id,
  //       status: selectedValue.id,
  //       orderRef: orderDetail.id.toString()
  //     })
  //   }
  // }, [selectedValue])
  return (
    <div className='bg-white border border-[#E9EAEB] rounded-[8px] sticky top-0 md:top-4 z-50'>
      {/* Row 1: identity + actions */}
      <div className='flex justify-between items-center gap-2 px-4 py-3'>
        <div className='flex items-center gap-2 min-w-0'>
          <div className='border rounded-[4px] p-1 cursor-pointer hover:bg-gray-100 transition-colors shrink-0'>
            <ArrowLeft size={12} strokeWidth={1} onClick={handleBack} />
          </div>
          <p className='text-body2 font-semibold text-gray-800 truncate'>{orderDetail.JobTitle}</p>
          {orderDetail.designType && (
            <span className='text-caption1 text-gray-500 shrink-0'>· {orderDetail.designType}</span>
          )}
        </div>
        <div className='flex items-center gap-2 shrink-0'>
          <div className="relative" ref={tagButtonRef}>
            <div
              className='border rounded-[4px] p-2 cursor-pointer bg-white hover:bg-gray-100 transition-colors'
              onClick={handleTagClick}
              title="Add Tags"
            >
              <Tag size={16} strokeWidth={1.5} className="text-gray-700" />
            </div>
            {showTagPopover && (
              <div className={`absolute right-0 z-50 ${positionAbove ? 'bottom-full mb-2' : 'top-full mt-2'}`}>
                <AddLabels
                  orderId={orderDetail.id}
                  orderRef={orderDetail.order_id}
                  customerId={orderDetail.customer_id}
                  labels={labels}
                  adminTags={adminTags}
                  fetchTags={fetchTags}
                  onUpdateOrder={onUpdateOrder}
                />
              </div>
            )}
          </div>
          {handlePrint && (
            <div
              className='border rounded-[4px] p-2 cursor-pointer bg-white hover:bg-gray-100 transition-colors mr-2'
              onClick={handlePrint}
              title="Download PDF"
            >
              <Printer size={16} strokeWidth={1.5} className="text-gray-700" />
            </div>
          )}
          <StatusDropDown {...{ selectedValue, setSelectedValue }} orderDetail={orderDetail} updateOrderStatus={updateOrderStatus} />
        </div>
      </div>

      {/* Row 2: order ref + tags, due date pinned right */}
      <div className='flex justify-between items-center gap-3 px-4 py-2 border-t border-[#E9EAEB]'>
        <div className='flex items-center gap-2 flex-wrap min-w-0'>
          {orderDetail.order_id && (
            <span className='text-caption1 font-normal text-gray-600'>#{orderDetail.order_id}</span>
          )}
          {labels.map((label) => (
            <span
              key={label}
              className='px-1.5 py-0.5 rounded-[4px] text-[11px] font-medium leading-none'
              style={labelStyle(label, adminTags)}
            >
              {label}
            </span>
          ))}
        </div>
        <div className='shrink-0'>
          <DueDatePicker value={localDueDate} onChange={handleDueDateChange} />
        </div>
      </div>
    </div>
  )
}

export default AdminHeader